import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { DropResult } from 'react-beautiful-dnd'
import keys from 'constants/keys'
import { Content, Subtask } from '../model/types'
import { useFormStepContext } from '../FormStepContext'
import { useBuilderSession } from '../contexts/BuilderSessionContext'
import { AI_IMAGE_BUTTON_TYPE } from '../model/subtaskTypes'

/** RHF v5 fields stay registered in the view; this hook owns only UI identity and leaf validation. */
export function useSubtaskBuilder(fields: (Subtask | Content)[], hasCategories = false) {
  const { watch, getValues, triggerValidation, errors } = useFormContext()
  const { subtaskActions, isMediaUploading, sumbitStep, isEdit } = useFormStepContext()
  const session = useBuilderSession()

  const values = watch({ nest: true }) as { title?: string; sub_tasks?: any[] }

  const [selectedId, setSelectedId] = useState<string | null>(() => {
    const saved = session?.current
    const index = saved && fields.length > saved.count ? fields.length - 1 : saved?.selected

    return index != null ? fields[index]?.id || null : null
  })
  const [hasStartedBuilding, setHasStartedBuilding] = useState(
    hasCategories || !!fields.length || !!session?.current?.hasStartedBuilding || !!session?.current?.hasCreatedSubtask
  )
  const [removalId, setRemovalId] = useState<string | null>(null)
  const [validating, setValidating] = useState(false)
  const [aiGenerating, setAiGenerating] = useState(false)

  // RHF v5 briefly unregisters indexed controllers during moves. AI identity belongs to the stable id.
  const aiIds = useRef(new Set<string>())

  fields.forEach((field, index) => {
    if (values.sub_tasks?.[index]?.is_ai_enhanced || (field as any).is_ai_enhanced) aiIds.current.add(field.id!)
  })

  const latestErrors = useRef(errors)
  latestErrors.current = errors

  const previousIds = useRef(fields.map((field) => field.id))
  const previouslyCreatedSubtask = useRef(!!session?.current?.hasCreatedSubtask)
  const titleRef = useRef<HTMLDivElement>(null)
  const titleStartRect = useRef<DOMRect | null>(null)
  const editorRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement | null>(null)

  const busy = isMediaUploading || validating || aiGenerating
  const selectedIndex = fields.findIndex((field) => field.id === selectedId)
  const selected = fields[selectedIndex]
  const selectedType = selected && aiIds.current.has(selected.id!) ? AI_IMAGE_BUTTON_TYPE : selected?.type
  const selectedAi = !isEdit && selectedType === AI_IMAGE_BUTTON_TYPE
  const hasSubtasks = fields.some((field) => field.type !== keys.SUBTASK_TYPES.DESCRIPTION)
  const hasCreatedSubtask = previouslyCreatedSubtask.current || fields.length > 0

  function startBuilding() {
    if (busy || !values.title?.trim() || values.title.length > 100) return
    titleStartRect.current = titleRef.current?.getBoundingClientRect() || null
    setHasStartedBuilding(true)
  }

  function requestRemove(index: number) {
    if (busy || isEdit) return
    setRemovalId(fields[index]?.id || null)
  }

  function confirmRemove() {
    if (busy || isEdit || !removalId) return
    const index = fields.findIndex((field) => field.id === removalId)
    setRemovalId(null)
    if (index >= 0) subtaskActions.remove?.(index)
  }

  function cancelRemove() {
    setRemovalId(null)
  }

  function select(id: string) {
    if (busy) return
    setSelectedId(id)
  }

  async function finish() {
    if (busy || !selected) return
    setValidating(true)

    // RHF v5 validates registered leaf names, not a parent object such as sub_tasks[0].
    const names = Object.keys(getValues()).filter((name) => name.startsWith(`sub_tasks[${selectedIndex}].`))

    try {
      await triggerValidation(names)
      setSelectedId(null)
    } finally {
      setValidating(false)
    }
  }

  async function continueToSettings() {
    if (busy) return
    setValidating(true)
    try {
      const valid = await triggerValidation()
      if (valid) {
        await sumbitStep()
      } else {
        const index = fields.findIndex((_, index) => latestErrors.current.sub_tasks?.[index])
        if (index >= 0) setSelectedId(fields[index].id!)
      }
    } finally {
      setValidating(false)
    }
  }

  function reorder({ source, destination }: DropResult) {
    if (busy || isEdit || !destination || source.index === destination.index) return
    subtaskActions.move?.(source.index, destination.index)
  }

  useLayoutEffect(() => {
    previouslyCreatedSubtask.current = hasCreatedSubtask
    if (session)
      session.current = {
        count: fields.length,
        selected: selectedIndex < 0 ? null : selectedIndex,
        hasCreatedSubtask,
        hasStartedBuilding,
      }
  }, [fields, selectedIndex, session, hasCreatedSubtask, hasStartedBuilding])

  useLayoutEffect(() => {
    const from = titleStartRect.current
    const node = titleRef.current
    titleStartRect.current = null
    if (!from || !node || !node.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const to = node.getBoundingClientRect()
    const animation = node.animate(
      [{ transform: `translate(${from.left - to.left}px, ${from.top - to.top}px)` }, { transform: 'translate(0, 0)' }],
      { duration: 420, easing: 'cubic-bezier(.2,.7,.2,1)' }
    )
    return () => animation.cancel()
  }, [hasStartedBuilding])

  useEffect(() => {
    const added = fields.find((field) => !previousIds.current.includes(field.id))

    if (added) {
      setSelectedId(added.id!)
    }
    previousIds.current = fields.map((field) => field.id)
    if (selectedId && !fields.some((field) => field.id === selectedId)) setSelectedId(null)
  }, [fields, selectedId])

  useEffect(() => {
    if (!selectedId) return
    if (editorRef.current) editorRef.current.scrollTop = 0
    listRef.current?.querySelector('[aria-pressed="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [selectedId])

  return {
    isEdit,
    values,
    errors,
    removalPending: !!removalId && fields.some((field) => field.id === removalId),
    requestRemove,
    confirmRemove,
    cancelRemove,
    subtaskActions,
    sumbitStep: continueToSettings,
    aiIds,
    selectedId,
    setSelectedId,
    editorRef,
    listRef,
    setAiGenerating,
    busy,
    selectedIndex,
    selected,
    selectedType,
    selectedAi,
    hasSubtasks,
    hasCreatedSubtask,
    hasStartedBuilding,
    startBuilding,
    titleRef,
    select,
    finish,
    reorder,
  }
}
